import { createStyles, makeStyles, Theme } from '@material-ui/core';
import AssignmentReturnedIcon from '@material-ui/icons/AssignmentReturned';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import MoreVertIconMUI from '@material-ui/icons/MoreVert';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import {
  CardType,
  FullAnswerFragment,
  QuestionDetailsFragment,
  useApproveAnswerMutation,
  useDeleteAnswerMutation,
} from '../../../../graphql/gen/types';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { colorPalette, spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { canChangeAnswerApprovalStatus, canDeleteAnswer } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';

type Props = {
  question: QuestionDetailsFragment;
  answer: FullAnswerFragment;
  loading: boolean;
  onLoadingChanged: (loading: boolean) => void;
};

export function MoreAnswerButton({ answer, question, loading, onLoadingChanged }: Props): React.ReactElement {
  const classes = useStyles();
  // TODO: handle error states
  const [deleteAnswer, { loading: deleteLoading }] = useDeleteAnswerMutation();
  const [approveAnswer, { loading: approveAnswerLoading }] = useApproveAnswerMutation();
  const confirmAction = useConfirmAction();
  useEffect(() => {
    onLoadingChanged(deleteLoading || approveAnswerLoading);
  }, [deleteLoading, approveAnswerLoading, onLoadingChanged]);

  const isApproved = question.acceptedAnswer?.id === answer.id;
  const plexRelation = answer.question.plex.currentUserRelation.state;
  const answerRelation = answer.currentUserRelation;

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this answer?',
      confirmButton: {
        onClick: (): void => {
          deleteAnswer({ variables: { answerId: answer.id } }).then(() =>
            logAnalytics({ name: 'answer deleted', details: { plexId: question.plex.id, itemId: answer.id } })
          );
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const handleApprove = (): void => {
    approveAnswer({ variables: { answerApproval: { answerId: answer.id, checked: true } } }).then(() =>
      logAnalytics({ name: 'answer approved', details: { plexId: question.plex.id, itemId: answer.id } })
    );
  };

  const handleRemoveApproval = (): void => {
    approveAnswer({ variables: { answerApproval: { answerId: answer.id, checked: false } } }).then(() =>
      logAnalytics({ name: 'answer unapproved', details: { plexId: question.plex.id, itemId: answer.id } })
    );
  };

  return (
    <MoreOptionsMenu
      disabled={loading}
      id={answer.id}
      Button={PlexIconButton}
      MuiIcon={MoreVertIconMUI}
      size={IconButtonSize.Normal}
      tooltip={'Open more options for this Answer'}
      color={ButtonColor.TransparentGray}
      options={[
        ...(canChangeAnswerApprovalStatus({ plexRelation })
          ? isApproved
            ? question.type === CardType.Generic
              ? []
              : [
                  {
                    label: (
                      <>
                        <AssignmentReturnedIcon className={classes.moreOptionIcon} />
                        <span>Remove approval</span>
                      </>
                    ),
                    onClick: handleRemoveApproval,
                  },
                ]
            : [
                {
                  label: (
                    <>
                      <AssignmentTurnedInIcon className={classes.moreOptionIcon} />
                      <span>Approve</span>
                    </>
                  ),
                  onClick: handleApprove,
                },
              ]
          : []),
        ...(canDeleteAnswer({ plexRelation, answerRelation })
          ? [
              {
                label: (
                  <>
                    <MuiDeleteIcon className={classNames(classes.moreOptionIcon, classes.deleteButton)} />
                    <span className={classes.deleteButton}>Delete</span>
                  </>
                ),
                onClick: handleDelete,
              },
            ]
          : []),
      ]}
      menuProps={{
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        transformOrigin: { horizontal: 'right', vertical: 'bottom' },
      }}
    />
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    moreOptionIcon: {
      fill: `${colorPalette.purpleGray._500} !important`,
      marginRight: spacings.small,
      width: IconButtonSize.Small,
      height: IconButtonSize.Small,
    },
    deleteButton: {
      fill: `${colorPalette.red._400} !important`,
      color: colorPalette.red._400,
    },
  })
);
